import type { ArtifactDescriptor } from '@/domain/release/types'
import { audienceEnvironment } from '@/core/runtime/environment'

const allowedArtifactHost = new URL(audienceEnvironment.cdnBase).hostname

export class VerifiedFetchError extends Error {
  constructor(
    message: string,
    public readonly code: 'NETWORK' | 'HTTP' | 'SIZE' | 'CHECKSUM' | 'JSON' | 'URL',
    public readonly status?: number,
  ) { super(message) }
}

export function assertSafeArtifactUrl(value: string, base = window.location.href): URL {
  const url = new URL(value, base)
  const sameOrigin = url.origin === window.location.origin
  if (url.protocol !== 'https:' && !(sameOrigin && url.protocol === 'http:')) throw new VerifiedFetchError('资源地址协议不受支持', 'URL')
  if (url.username || url.password || (!sameOrigin && url.hostname !== allowedArtifactHost)) throw new VerifiedFetchError('资源地址不属于当前构建环境', 'URL')
  return url
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

export async function fetchVerifiedJson(
  url: string,
  options: { sha256?: string; bytes?: number; timeoutMs?: number; cache?: RequestCache } = {},
  fetcher: typeof fetch = fetch,
): Promise<unknown> {
  const safeUrl = assertSafeArtifactUrl(url)
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), options.timeoutMs ?? 12_000)
  try {
    const response = await fetcher(safeUrl, { cache: options.cache ?? 'no-store', credentials: 'omit', signal: controller.signal })
    if (!response.ok) throw new VerifiedFetchError(`HTTP ${response.status}`, 'HTTP', response.status)
    const text = await response.text()
    const bytes = new TextEncoder().encode(text).byteLength
    if (options.bytes !== undefined && bytes !== options.bytes) throw new VerifiedFetchError(`资源字节数不符：${bytes}`, 'SIZE')
    if (options.sha256 && (await sha256Hex(text)).toLowerCase() !== options.sha256.toLowerCase()) throw new VerifiedFetchError('资源校验和不符', 'CHECKSUM')
    try { return JSON.parse(text) as unknown } catch { throw new VerifiedFetchError('资源不是有效 JSON', 'JSON') }
  } catch (error) {
    if (error instanceof VerifiedFetchError) throw error
    throw new VerifiedFetchError(error instanceof Error ? error.message : '网络请求失败', 'NETWORK')
  } finally { window.clearTimeout(timer) }
}

export function fetchArtifact(descriptor: ArtifactDescriptor, fetcher: typeof fetch = fetch): Promise<unknown> {
  return fetchVerifiedJson(descriptor.url, { sha256: descriptor.sha256, bytes: descriptor.bytes, cache: 'force-cache' }, fetcher)
}
